import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store/appStore';
import { getOutputJsonTaskStatus, setClearSaveJsonStatus, setTaskStatus } from '../../redux/slices/outputJson';
import { constants } from '../../utils/constants';
import { STATUS } from '../../utils/status';
import { getCookie } from '../login/cookies';
import TaskStatusModal from '../outputJson/taskStatusModal';

type ExecutionStatus = 'idle' | 'uploading' | 'in_progress' | 'success' | 'error';

const ALERT_SEVERITY: Record<ExecutionStatus, 'info' | 'success' | 'error'> = {
  idle: 'info',
  uploading: 'info',
  in_progress: 'info',
  success: 'success',
  error: 'error',
};

const RunScriptForm: React.FC<{ projectId?: string; onSuccess?: () => void; onRunStarted?: () => void }> = ({ projectId, onSuccess, onRunStarted }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = projectId ?? paramId;
  const isGlobal = !id;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch<AppDispatch>();

  const taskStatusOutputJsonResponse = useSelector(
    (state: RootState) => state.outputJson.taskStatusOutputJsonResponse
  );
  const getTaskStatusOutputJson = useSelector(
    (state: RootState) => state.outputJson.getTaskStatusOutputJson
  );
  const getTaskStatusOutputJsonMessage = useSelector(
    (state: RootState) => state.outputJson.getTaskStatusOutputJsonMessage
  );

  const [scriptName, setScriptName] = useState('');
  const [scriptFile, setScriptFile] = useState<File | null>(null);
  const [uploadedScriptUrl, setUploadedScriptUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [sourceFolderPath, setSourceFolderPath] = useState('');
  const [outputFolderPath, setOutputFolderPath] = useState('');
  const [executionStatus, setExecutionStatus] = useState<ExecutionStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [pollingTaskId, setPollingTaskId] = useState<string | undefined>(undefined);
  const [pollingModalOpen, setPollingModalOpen] = useState(false);
  const [sourcePathApiError, setSourcePathApiError] = useState<string | null>(null);
  const [outputPathApiError, setOutputPathApiError] = useState<string | null>(null);

  const pollingStatus: string | undefined = taskStatusOutputJsonResponse?.data?.status;
  const TERMINAL_STATUSES = ['SUCCESS', 'FAILURE', 'TASK_FAILED'];

  // Poll every 10s until a terminal status is reached
  useEffect(() => {
    if (!pollingTaskId || !pollingModalOpen) return;
    if (pollingStatus && TERMINAL_STATUSES.includes(pollingStatus)) return;

    const intervalId = setInterval(() => {
      dispatch(getOutputJsonTaskStatus({ celery_task_id: pollingTaskId }));
    }, 10000);

    return () => clearInterval(intervalId);
  }, [pollingTaskId, pollingModalOpen, pollingStatus, dispatch]);

  // Handle terminal states
  useEffect(() => {
    if (pollingStatus === 'SUCCESS') {
      setExecutionStatus('success');
      setStatusMessage(
        'Script executed successfully. Output has been saved to the selected output folder.'
      );
    } else if (pollingStatus === 'FAILURE' || pollingStatus === 'TASK_FAILED') {
      setExecutionStatus('error');
      setStatusMessage('Script execution failed. Please review the error details and try again.');
    }
  }, [pollingStatus]);

  useEffect(() => {
    if (!pollingModalOpen || pollingStatus) return;

    if (getTaskStatusOutputJson === STATUS.FAILED) {
      setExecutionStatus('error');
      setStatusMessage(
        getTaskStatusOutputJsonMessage ||
          'Unable to retrieve the latest script execution status. Please review the error details and try again.'
      );
    }
  }, [pollingModalOpen, pollingStatus, getTaskStatusOutputJson, getTaskStatusOutputJsonMessage]);

  // Normalise status for modal — map any non-terminal status to 'PENDING' so the
  // modal's loading state renders correctly for PROGRESS / RUNNING_SCRIPT etc.
  // Map TASK_FAILED → FAILURE so the modal renders its error state.
  // Fall back to { status: 'PENDING' } while the modal is open but the first
  // poll hasn't returned yet (Redux state is empty after setTaskStatus({})).
  const normalizedModalData = taskStatusOutputJsonResponse?.data
    ? {
        ...taskStatusOutputJsonResponse.data,
        status: pollingStatus === 'SUCCESS'
          ? 'SUCCESS'
          : (pollingStatus === 'FAILURE' || pollingStatus === 'TASK_FAILED')
          ? 'FAILURE'
          : 'PENDING',
      }
    : pollingModalOpen && getTaskStatusOutputJson === STATUS.FAILED
    ? {
        status: 'FAILURE' as const,
        data: {
          detail: getTaskStatusOutputJsonMessage ?? null,
        },
        error: taskStatusOutputJsonResponse?.error ?? null,
        message: taskStatusOutputJsonResponse?.message ?? null,
        result: taskStatusOutputJsonResponse?.result ?? null,
      }
    : pollingModalOpen
    ? { status: 'PENDING' as const }
    : null;

  const isFormValid =
    uploadedScriptUrl !== null &&
    !isUploading &&
    sourceFolderPath.trim() !== '' &&
    outputFolderPath.trim() !== '' &&
    (!isGlobal || scriptName.trim() !== '');

  const isRunDisabled = !isFormValid || executionStatus === 'in_progress';

  const applyFile = async (file: File | null) => {
    if (!file) {
      setScriptFile(null);
      setUploadedScriptUrl(null);
      setUploadError(null);
      return;
    }
    setScriptFile(file);
    setUploadedScriptUrl(null);
    setUploadError(null);
    setIsUploading(true);
    try {
      const uploadForm = new FormData();
      uploadForm.append('file', file);
      uploadForm.append('bucket', 'custom');
      const uploadResponse = await fetch(`${constants.API_BASE_URL}upload/company/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getCookie('token')}` },
        body: uploadForm,
      });
      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}`);
      }
      const uploadData = await uploadResponse.json();
      setUploadedScriptUrl(uploadData.file_url);
    } catch {
      setUploadError('Failed to upload script. Please try again.');
      setScriptFile(null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyFile(e.target.files?.[0] ?? null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    applyFile(e.dataTransfer.files?.[0] ?? null);
  };

  const toS3Uri = (url: string): string => {
    try {
      const { hostname, pathname } = new URL(url);
      const bucketName = hostname.split('.s3.')[0];
      return `s3://${bucketName}${pathname}`;
    } catch {
      return url;
    }
  };

  const handleRunScript = async () => {
    if (!isFormValid || !uploadedScriptUrl) return;

    setExecutionStatus('in_progress');
    setStatusMessage('Script execution initiated.');
    setSourcePathApiError(null);
    setOutputPathApiError(null);

    try {
      const endpoint = isGlobal
        ? `${constants.API_BASE_URL}project/script-execution/run/`
        : `${constants.API_BASE_URL}project/company/script/run/`;

      const payload = isGlobal
        ? {
            script_name: scriptName.trim(),
            script_path: toS3Uri(uploadedScriptUrl),
            source_file_path: sourceFolderPath.trim(),
            output_path: outputFolderPath.trim(),
          }
        : {
            ...(id && { project_id: id }),
            script_path: toS3Uri(uploadedScriptUrl),
            source_file_path: sourceFolderPath.trim(),
            output_path: outputFolderPath.trim(),
          };

      const runResponse = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${getCookie('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!runResponse.ok) {
        const errorData = await runResponse.json().catch(() => ({}));
        const sourceErr: string | null = Array.isArray(errorData?.source_file_path)
          ? (errorData.source_file_path[0] as string)
          : null;
        const outputErr: string | null = Array.isArray(errorData?.output_path)
          ? (errorData.output_path[0] as string)
          : null;
        if (sourceErr || outputErr) {
          if (sourceErr) setSourcePathApiError(sourceErr);
          if (outputErr) setOutputPathApiError(outputErr);
          setExecutionStatus('error');
          setStatusMessage('Please fix the path errors highlighted below and try again.');
        } else {
          throw new Error(`Run script failed with status ${runResponse.status}`);
        }
        return;
      }

      const runData = await runResponse.json();

      const bgTaskId: string = runData?.data?.bg_task_id;
      if (bgTaskId) {
        dispatch(setTaskStatus({}));
        setPollingTaskId(bgTaskId);
        setPollingModalOpen(true);
        setStatusMessage('Script execution in progress. Please wait...');
        dispatch(getOutputJsonTaskStatus({ celery_task_id: bgTaskId }));
        if (isGlobal) onRunStarted?.();
      } else {
        setExecutionStatus('error');
        setStatusMessage('Script execution failed. Please review the error details and try again.');
      }
    } catch {
      setExecutionStatus('error');
      setStatusMessage('Script execution failed. Please review the error details and try again.');
    }
  };

  const dropzoneStyles = {
    border: `2px dashed ${isDragOver ? '#3D85C6' : uploadError ? '#d32f2f' : isUploading ? '#42a5f5' : uploadedScriptUrl ? '#4caf50' : '#b0bec5'}`,
    borderRadius: '10px',
    p: 3,
    textAlign: 'center' as const,
    cursor: isUploading ? 'not-allowed' : 'pointer',
    backgroundColor: isDragOver ? '#f0f7ff' : uploadError ? '#fff5f5' : isUploading ? '#f0f7ff' : uploadedScriptUrl ? '#f1faf1' : '#fafbfc',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: isUploading ? undefined : '#3D85C6',
      backgroundColor: isUploading ? undefined : '#f0f7ff',
    },
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover fieldset': { borderColor: '#3D85C6' },
      '&.Mui-focused fieldset': { borderColor: '#3D85C6' },
    },
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720 }}>
      {/* Script Name — only shown in global (non-project) context */}
      {isGlobal && (
        <Box mb={3}>
          <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D', mb: 1 }}>
            Script Name <span style={{ color: '#d32f2f' }}>*</span>
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. DL Image Validation"
            value={scriptName}
            onChange={(e) => setScriptName(e.target.value)}
            disabled={executionStatus === 'in_progress'}
            size="small"
            sx={textFieldSx}
          />
        </Box>
      )}

      {/* Script Upload */}
      <Box mb={3}>
        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D', mb: 1 }}>
          Script File <span style={{ color: '#d32f2f' }}>*</span>
        </Typography>

        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          sx={dropzoneStyles}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".py,.sh,.js,.ts,.rb,.r,.bat,.cmd,.ps1"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />

          {isUploading ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <CircularProgress size={24} sx={{ color: '#3D85C6' }} />
              <Box textAlign="left">
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D' }}>
                  {scriptFile?.name}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#3D85C6' }}>
                  Uploading...
                </Typography>
              </Box>
            </Box>
          ) : uploadError ? (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 40, color: '#d32f2f', mb: 1 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#d32f2f' }}>
                {uploadError}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#90a4ae', mt: 0.5 }}>
                Click to try again
              </Typography>
            </Box>
          ) : scriptFile && uploadedScriptUrl ? (
            <Box display="flex" alignItems="center" justifyContent="center" gap={1.5}>
              <InsertDriveFileOutlinedIcon sx={{ color: '#4caf50', fontSize: 32 }} />
              <Box textAlign="left">
                <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D' }}>
                  {scriptFile.name}
                </Typography>
                <Typography sx={{ fontSize: '12px', color: '#78909c' }}>
                  {(scriptFile.size / 1024).toFixed(1)} KB — Click to replace
                </Typography>
              </Box>
              <CheckCircleOutlineIcon sx={{ color: '#4caf50', ml: 1 }} />
            </Box>
          ) : (
            <Box>
              <CloudUploadIcon sx={{ fontSize: 40, color: '#90a4ae', mb: 1 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#546e7a' }}>
                Click to upload or drag &amp; drop
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#90a4ae', mt: 0.5 }}>
                Supported: .py, .sh, .js, .ts, .rb, .r, .bat, .ps1
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Source Folder Path */}
      <Box mb={3}>
        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D', mb: 1 }}>
          Source Folder Path <span style={{ color: '#d32f2f' }}>*</span>
        </Typography>
        <Tooltip
          title={!uploadedScriptUrl && !isUploading ? 'Upload a script first to set the path' : ''}
          placement="top-start"
          arrow
        >
          <span>
            <TextField
              fullWidth
              placeholder="e.g. /data/input or s3://bucket/input/"
              value={sourceFolderPath}
              onChange={(e) => { setSourceFolderPath(e.target.value); setSourcePathApiError(null); }}
              disabled={!uploadedScriptUrl || isUploading || executionStatus === 'in_progress'}
              size="small"
              error={!!sourcePathApiError}
              helperText={sourcePathApiError ?? undefined}
              sx={textFieldSx}
            />
          </span>
        </Tooltip>
      </Box>

      {/* Output Folder Path */}
      <Box mb={3}>
        <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#10383D', mb: 1 }}>
          Output Folder Path <span style={{ color: '#d32f2f' }}>*</span>
        </Typography>
        <Tooltip
          title={!uploadedScriptUrl && !isUploading ? 'Upload a script first to set the path' : ''}
          placement="top-start"
          arrow
        >
          <span>
            <TextField
              fullWidth
              placeholder="e.g. /data/output or s3://bucket/output/"
              value={outputFolderPath}
              onChange={(e) => { setOutputFolderPath(e.target.value); setOutputPathApiError(null); }}
              disabled={!uploadedScriptUrl || isUploading || executionStatus === 'in_progress'}
              size="small"
              error={!!outputPathApiError}
              helperText={outputPathApiError ?? undefined}
              sx={textFieldSx}
            />
          </span>
        </Tooltip>
      </Box>

      {/* Run Script Button */}
      <Box mb={3}>
        <Button
          variant="contained"
          disabled={isRunDisabled}
          onClick={handleRunScript}
          startIcon={
            executionStatus === 'in_progress' ? (
              <CircularProgress size={18} sx={{ color: '#fff' }} />
            ) : (
              <PlayArrowRoundedIcon />
            )
          }
          sx={{
            height: '44px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 600,
            textTransform: 'none',
            px: 4,
            background: isRunDisabled ? undefined : 'linear-gradient(45deg, #478ed1, #42a5f5)',
            '&:hover': {
              background: 'linear-gradient(45deg, #3679b8, #2196f3)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#e0e0e0',
              color: '#9e9e9e',
            },
          }}
        >
          {executionStatus === 'in_progress' ? 'Running...' : 'Run Script'}
        </Button>
      </Box>

      {/* Status Message */}
      {statusMessage && (
        <Alert
          severity={ALERT_SEVERITY[executionStatus]}
          variant="outlined"
          sx={{ borderRadius: '8px', fontSize: '14px' }}
        >
          {statusMessage}
        </Alert>
      )}

      <TaskStatusModal
        open={pollingModalOpen}
        onClose={() => {
          setPollingModalOpen(false);
          if (isGlobal) {
            dispatch(setClearSaveJsonStatus({}));
            onSuccess?.();
          }
        }}
        taskStatusData={normalizedModalData}
        fileUpload
        scriptExecution={isGlobal}
      />
    </Box>
  );
};

export default RunScriptForm;
